/**
 * CareerStatsCard — Shows career highlights on an athlete's profile.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { fetchCareerStats, type CareerStats } from '@/lib/careerStats';
import { Swords, Trophy, Dumbbell, Flame, Users, Lock } from 'lucide-react-native';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';

interface CareerStatsCardProps {
  profileId: string;
}

export default function CareerStatsCard({ profileId }: CareerStatsCardProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { tier } = useSubscription();
  const isPro = hasAtLeast(tier, 'pro');
  const [stats, setStats] = useState<CareerStats | null>(null);

  useEffect(() => {
    if (!profileId) return;
    fetchCareerStats(profileId).then(setStats).catch(() => {});
  }, [profileId]);

  if (!isPro) {
    return (
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[styles.title, { color: c.accent, fontFamily: TYPO.heading.bold }]}>CAREER STATS</Text>
        <View style={styles.lockedContainer}>
          <Lock size={24} color={c.textTertiary} />
          <Text style={[styles.lockedText, { color: c.textSecondary }]}>
            Track your complete lifting career with Pro
          </Text>
        </View>
      </View>
    );
  }

  if (!stats) return null;

  const items: { icon: any; label: string; value: string }[] = [
    { icon: Dumbbell, label: 'Workouts', value: String(stats.total_workouts) },
    { icon: Swords, label: 'Duel Record', value: `${stats.duel_wins}W-${stats.duel_losses}L` },
    { icon: Trophy, label: 'Tournament Wins', value: String(stats.tournament_wins) },
    { icon: Flame, label: 'Best Streak', value: `${stats.best_workout_streak} days` },
    { icon: Users, label: 'Followers', value: String(stats.followers_count) },
  ];

  // Best lifts
  const lifts: { label: string; value: string }[] = [];
  if (stats.best_squat_kg) lifts.push({ label: 'Squat', value: `${Math.round(stats.best_squat_kg)}kg` });
  if (stats.best_bench_kg) lifts.push({ label: 'Bench', value: `${Math.round(stats.best_bench_kg)}kg` });
  if (stats.best_deadlift_kg) lifts.push({ label: 'Deadlift', value: `${Math.round(stats.best_deadlift_kg)}kg` });
  if (stats.best_total_kg) lifts.push({ label: 'Total', value: `${Math.round(stats.best_total_kg)}kg` });

  return (
    <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
      <Text style={[styles.title, { color: c.accent, fontFamily: TYPO.heading.bold }]}>CAREER STATS</Text>

      <View style={styles.statsGrid}>
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <View key={idx} style={styles.statItem}>
              <Icon size={14} color={c.textTertiary} />
              <Text style={[styles.statValue, { color: c.text, fontFamily: TYPO.body.semibold }]}>{item.value}</Text>
              <Text style={[styles.statLabel, { color: c.textTertiary }]}>{item.label}</Text>
            </View>
          );
        })}
      </View>

      {lifts.length > 0 && (
        <>
          <View style={[styles.divider, { backgroundColor: c.border }]} />
          <Text style={[styles.subtitle, { color: c.accent, fontFamily: TYPO.heading.bold }]}>BEST LIFTS</Text>
          <View style={styles.liftsRow}>
            {lifts.map((lift, idx) => (
              <View key={idx} style={[styles.liftChip, { backgroundColor: c.surfaceVariant }]}>
                <Text style={[styles.liftValue, { color: c.text, fontFamily: TYPO.body.semibold }]}>{lift.value}</Text>
                <Text style={[styles.liftLabel, { color: c.textTertiary }]}>{lift.label}</Text>
              </View>
            ))}
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 16, gap: SPACE.sm },
  title: { fontSize: 11, letterSpacing: 1.5 },
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACE.md },
  statItem: { alignItems: 'center', gap: 2, minWidth: 70 },
  statValue: { fontSize: 16 },
  statLabel: { fontSize: 11, letterSpacing: 0.3 },
  divider: { height: 1, marginVertical: 4 },
  subtitle: { fontSize: 11, letterSpacing: 1.5 },
  liftsRow: { flexDirection: 'row', gap: SPACE.sm },
  liftChip: { flex: 1, alignItems: 'center', paddingVertical: 8, borderRadius: RADIUS.xs, gap: 2 },
  liftValue: { fontSize: 15 },
  liftLabel: { fontSize: 11 },
  lockedContainer: { alignItems: 'center', gap: 8, paddingVertical: 20 },
  lockedText: { fontSize: 13, textAlign: 'center' },
});
